import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Stack, IconButton, Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { ProcedureGrid } from '../grids';
import { AddProcedureDialog, ProcedureUploadHelpDialog } from '../dialogs';
import { ProcedureUploadCsv, ProcedureDownloadCsv } from '../commands';
import { useGetProceduresQuery } from '../../../../services/ConfigurationService';
import { UploadProgress } from '../../../../services/signalRService';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const ProceduresTab: React.FC = () => {
  const { id: centerId } = useParams<{ id: string }>();
  const query = useQuery();
  const company = query.get("company") || "";
  
  // Fetch procedures data
  const { data: procedures = [], refetch: refetchProcedures } = useGetProceduresQuery({ centerId: centerId || '' }, { skip: !centerId });
  
  // Upload progress state
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  
  // Help dialog state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // Upload handlers
  const handleUploadStart = () => {
    setProgress({
      operationId: '',
      progressPercentage: 0,
      message: 'Starting upload...',
      processedRecords: 0,
      totalRecords: 0,
      timestamp: new Date().toISOString(),
      status: 'Starting',
      uploadType: 'procedures'
    });
  };
  
  const handleProgressUpdate = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadComplete = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadError = (error: string) => {
    console.error('Procedure upload error:', error);
    setProgress(null);
  };
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Tooltip title="View upload file specification">
          <IconButton 
            onClick={() => setHelpDialogOpen(true)}
            size="small"
            color="primary"
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        
        <ProcedureDownloadCsv
          company={company}
          centerId={centerId || ''}
          onError={(error) => console.error('Download error:', error)}
        />
        <ProcedureUploadCsv
          company={company}
          centerId={centerId || ''}
          onUploadStart={handleUploadStart}
          onProgressUpdate={handleProgressUpdate}
          onComplete={handleUploadComplete}
          onSuccess={() => {
            refetchProcedures();
            console.log('Procedures uploaded successfully');
          }}
          onError={handleUploadError}
        />
        <AddProcedureDialog
          defaultCenterId={centerId || ''}
          onSuccess={refetchProcedures}
        />
      </Box>
      <ProcedureGrid
        procedures={procedures}
        centerId={centerId || ''}
        onProcedureSaved={refetchProcedures}
      />
      
      {/* Help Dialog */}
      <ProcedureUploadHelpDialog 
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
    </Stack>
  );
};